import { Component, Input, ChangeDetectionStrategy, HostBinding } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LucideAngularModule } from 'lucide-angular';

export type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

export interface BadgeItem {
  text: string;
  variant?: BadgeVariant;
  color?: string;
  bg?: string;
}

@Component({
  selector: 'app-section-card',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './section-card.component.html',
  styleUrls: ['./section-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionCardComponent {
  @Input() icon?: any;
  @Input() hasBackgroundIcon = true;
  @Input({ required: true }) title!: string;
  @Input() subtitle = '';
  @Input() extraText = '';
  @Input() badge = '';
  @Input() bottomBadges: BadgeItem[] = [];

  // --- COLORES --- (opcionales: si no se pasan, mandan los del theme/SCSS)
  @Input() titleColor?: string;
  @Input() subtitleColor?: string;
  @Input() extraColor?: string;

  // --- BADGE SUPERIOR ---
  @Input() badgeVariant: BadgeVariant = 'default';
  @Input() badgeColor?: string;
  @Input() badgeBgColor?: string;

  private readonly badgeVariants: Record<BadgeVariant, { color: string; bg: string }> = {
    default: { color: '#374151', bg: '#e5e7eb' },
    success: { color: '#166534', bg: '#dcfce7' },
    warning: { color: '#92400e', bg: '#fef3c7' },
    danger: { color: '#991b1b', bg: '#fee2e2' },
    info: { color: '#1e40af', bg: '#dbeafe' },
  };

  @Input() titleSize = '0.9375rem';
  @Input() subtitleSize = '0.8125rem';
  @Input() extraSize = '0.75rem';

  @Input() headerBgColor?: string; // opcional también
  @Input() iconBgColor = '#DBEAFE';
  @Input() iconColor = '#2563EB';

  @Input() variant: 'default' | 'compact' = 'default';
  @Input() hasFooter = false;
  @Input() width = '100%';
  @Input() height = 'auto';

  @Input() divider = true;
  @Input() headerBorder = true;
  @Input() extraDivider = false;

  get resolvedBadgeColor(): string {
    return this.badgeColor ?? this.badgeVariants[this.badgeVariant].color;
  }

  get resolvedBadgeBg(): string {
    return this.badgeBgColor ?? this.badgeVariants[this.badgeVariant].bg;
  }

  resolveBottomBadgeColor(b: BadgeItem): string {
    if (b.color) return b.color;
    const variant = b.variant ?? 'default';
    return this.badgeVariants[variant].color;
  }

  resolveBottomBadgeBg(b: BadgeItem): string {
    if (b.bg) return b.bg;
    const variant = b.variant ?? 'default';
    return this.badgeVariants[variant].bg;
  }

  // ===== HostBindings =====
  // Solo inyecta la variable si el input viene con valor.
  // Si no viene, devuelve null → Angular NO pinta el estilo → SCSS decide.

  @HostBinding('style.--header-bg') get _headerBg() {
    return this.headerBgColor ?? null;
  }
  @HostBinding('style.--title-size') get _titleSize() {
    return this.titleSize || null;
  }
  @HostBinding('style.--subtitle-size') get _subtitleSize() {
    return this.subtitleSize || null;
  }
  @HostBinding('style.--extra-size') get _extraSize() {
    return this.extraSize || null;
  }
  @HostBinding('style.--title-color') get _titleColor() {
    return this.titleColor ?? null;
  }
  @HostBinding('style.--subtitle-color') get _subtitleColor() {
    return this.subtitleColor ?? null;
  }
  @HostBinding('style.--extra-color') get _extraColor() {
    return this.extraColor ?? null;
  }

  @HostBinding('style.--badge-color') get _badgeColor() {
    return this.resolvedBadgeColor ?? null;
  }
  @HostBinding('style.--badge-bg') get _badgeBg() {
    return this.resolvedBadgeBg ?? null;
  }

  @HostBinding('style.--header-border') get _headerBorder() {
    return this.headerBorder ? '2px solid #c3c6cf' : 'none';
  }
}
